import { FormatDateForDateInput } from './Dates/datesForDateInput'
import formatPhone from './formatPhone'
import { normalizeWorkWith } from './workWithLabels'
import type { WorkWithKind } from './workWithLabels'

export const HUB_CUSTOM_FIELD_PREFIX = 'CF_'
// Mirrors WorkHubListCustomFields.FieldValueMaxLength (CustomFieldsConnections.Value is NVarChar(200)).
export const HUB_CUSTOM_FIELD_MAX_LENGTH = 200

// Mirrors CustomFieldEnumType in Fundraising.BusinessLayer.
export const CustomFieldType = {
    Text: 0,
    CheckBox: 1,
    List: 2,
    Date: 3,
    Phone: 4,
    CheckedDropDown: 5,
    CheckedDropDownNew: 6,
    LongText: 7,
    NumberRange: 8,
} as const

export type HubCustomFieldColumn = {
    FieldName: string
    CustomFieldId: number
    Label?: string | null
    Type: number
    Options?: string | null
    Hebrew?: boolean
    DefaultValue?: string | null
    SaveText?: boolean
    DateFormat?: string | null
}

export type HubCustomFieldLink = {
    DonorID?: number | null
    ParentID?: number | null
    StudentID?: number | null
}

export type NamedOption = { value: number; name: string }
export type MultiOption = { id: number; label: string }
export type NumberRangeValue = { from: string; to: string; parity: number }

export const PARITY_OPTIONS: NamedOption[] = [
    { value: 0, name: 'Even' },
    { value: 1, name: 'Odd' },
    { value: 2, name: 'None' },
]

export function isHubCustomFieldName(name: string | null | undefined): boolean {
    if (!name || name.length <= HUB_CUSTOM_FIELD_PREFIX.length) return false
    if (name.slice(0, HUB_CUSTOM_FIELD_PREFIX.length).toUpperCase() !== HUB_CUSTOM_FIELD_PREFIX) return false
    const id = Number(name.slice(HUB_CUSTOM_FIELD_PREFIX.length))
    return Number.isInteger(id) && id > 0
}

export function columnNameForCustomField(fieldId: number): string {
    return HUB_CUSTOM_FIELD_PREFIX + fieldId
}

export function linkIdForTask(
    workWith: string | null | undefined,
    task: HubCustomFieldLink | null | undefined
): number {
    if (!task) return 0
    const kind: WorkWithKind = normalizeWorkWith(workWith)
    const raw = kind === 'students' ? task.StudentID : task.DonorID
    const id = Number(raw)
    return Number.isInteger(id) && id > 0 ? id : 0
}

export function splitPipe(value: string | null | undefined): string[] {
    if (!value) return []
    return value
        .split('|')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
}

export function joinPipe(values: string[]): string {
    return (values || []).map((part) => (part || '').trim()).filter((part) => part.length > 0).join('|')
}

export function isCheckedValue(value: string | null | undefined): boolean {
    return (value || '').toString().toLowerCase() === 'true'
}

export function checkboxStoredValue(checked: boolean): string {
    return checked ? 'True' : 'False'
}

function officeParts(format: string | null | undefined): { order: 'day' | 'month' | 'year'; sep: string } {
    const text = format || 'MM/dd/yyyy'
    const upper = text.toUpperCase()
    const sep = text.indexOf('-') >= 0 ? '-' : text.indexOf('.') >= 0 ? '.' : '/'
    const order = upper.indexOf('YYYY') === 0 ? 'year' : upper.indexOf('DD') === 0 ? 'day' : 'month'
    return { order, sep }
}

function checkedIso(year: number, month: number, day: number): string {
    if (month < 1 || month > 12 || day < 1 || day > 31) return ''
    const check = new Date(year, month - 1, day)
    if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) return ''
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function officeDateToIso(value: string | null | undefined, format?: string | null): string {
    const raw = (value || '').trim()
    const { order } = officeParts(format)
    if (order === 'year') {
        const yearFirst = /^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})/.exec(raw)
        if (!yearFirst) return ''
        return checkedIso(Number(yearFirst[1]), Number(yearFirst[2]), Number(yearFirst[3]))
    }
    const match = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/.exec(raw)
    if (!match) return ''
    const day = Number(order === 'day' ? match[1] : match[2])
    const month = Number(order === 'day' ? match[2] : match[1])
    return checkedIso(Number(match[3]), month, day)
}

export function isoToOfficeDate(value: string | null | undefined, format?: string | null): string {
    const raw = (value || '').trim()
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
    if (!match) return raw
    const { order, sep } = officeParts(format)
    if (order === 'year') return `${match[1]}${sep}${match[2]}${sep}${match[3]}`
    return order === 'day' ? `${match[3]}${sep}${match[2]}${sep}${match[1]}` : `${match[2]}${sep}${match[3]}${sep}${match[1]}`
}

export function dateValueForInput(value: string | null | undefined, format?: string | null): string {
    const raw = (value || '').trim()
    if (!raw) return ''
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
    const office = officeDateToIso(raw, format)
    if (office) return office
    const formatted = FormatDateForDateInput(raw) as string
    if (!formatted || formatted.indexOf('NaN') >= 0) return ''
    return formatted
}

export function listOptions(options: string | null | undefined): NamedOption[] {
    return [{ value: -1, name: '' }, ...splitPipe(options).map((name, value) => ({ value, name }))]
}

export function selectedListValue(options: NamedOption[], current: string | null | undefined): number {
    const match = options.find((option) => option.value !== -1 && option.name === (current || ''))
    return match ? match.value : -1
}

export function freeTextOptions(options: string | null | undefined): NamedOption[] {
    return splitPipe(options).map((name, value) => ({ value, name }))
}

export function multiItems(options: string | null | undefined): MultiOption[] {
    return splitPipe(options).map((label, id) => ({ id, label }))
}

export function multiInitialIds(items: MultiOption[], value: string | null | undefined): number[] {
    const selected = new Set(splitPipe(value))
    return items.filter((item) => selected.has(item.label)).map((item) => item.id)
}

export function multiValueFromIds(items: MultiOption[], ids: number[]): string {
    const wanted = new Set(ids)
    return joinPipe(items.filter((item) => wanted.has(item.id)).map((item) => item.label))
}

export function parseNumberRange(value: string | null | undefined): NumberRangeValue {
    const parts = (value || '').split('|')
    const parity = parts[2] === '0' || parts[2] === '1' || parts[2] === '2' ? Number(parts[2]) : 2
    return { from: parts[0] || '', to: parts[1] || '', parity }
}

export function numberRangeToStore(range: NumberRangeValue): { ok: boolean; value: string } {
    const from = (range.from || '').trim()
    const to = (range.to || '').trim()
    const parity = range.parity === 0 || range.parity === 1 || range.parity === 2 ? range.parity : 2
    if (from === '' && to === '') return { ok: true, value: `0|0|${parity}` }
    if (!/^-?\d+$/.test(from) || !/^-?\d+$/.test(to)) return { ok: false, value: '' }
    return { ok: true, value: `${from}|${to}|${parity}` }
}

export function formatNumberRange(range: NumberRangeValue): string {
    return `${range.from || ''}|${range.to || ''}|${range.parity}`
}

export function hubCustomFieldReadout(
    type: number,
    value: string | null | undefined,
    dateFormat?: string | null
): string {
    const raw = value || ''
    if (type === CustomFieldType.Date) {
        const iso = dateValueForInput(raw, dateFormat)
        return iso ? isoToOfficeDate(iso, dateFormat) : raw
    }
    if (type === CustomFieldType.CheckBox) {
        if (!raw) return ''
        return isCheckedValue(raw) ? 'Yes' : 'No'
    }
    if (type === CustomFieldType.Phone) return formatPhone(raw) || ''
    if (type === CustomFieldType.CheckedDropDown || type === CustomFieldType.CheckedDropDownNew) return splitPipe(raw).join(', ')
    if (type === CustomFieldType.NumberRange) {
        if (!raw.trim()) return ''
        const range = parseNumberRange(raw)
        const span = [range.from, range.to].filter((part) => part !== '').join(' – ')
        const parity = range.parity === 0 ? 'Even' : range.parity === 1 ? 'Odd' : ''
        return [span, parity].filter((part) => part !== '').join(' ')
    }
    return raw
}
